import { env } from '../../../env';
import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import APIService from '../../../services/api.service';
import { AxiosError } from 'axios';
import { Item } from '../types/item.types';
import { ModifyResponse } from '../types/items.api';
import { Query } from '../../../shared/types/query.types';
import { SubItem } from '../types/sub-item.types';
import { ToastService } from '../../../services/toast.service';
import { convertBackendItemToFrontendItem } from '../../../types/item.mapper';
import create from 'zustand';
import { equals } from 'lodash/fp';
import { flatten } from 'lodash';
import shallow from 'zustand/shallow';
import { useBomVersionState } from '../../bom/state/bom-version.state';
import { useExpandedState } from './expanded-rows.state';
import { useMsal } from '@azure/msal-react';
import { useSelectionState } from './selection.state';

export type DataState = {
   data: Item[];
   isLoading: boolean;
   total: number;
   query: Query;
   setQuery: (query: Query | ((currentQuery: Query) => Query)) => void;
   progress: number;
   updateItemInTable: (itemNo: string, updatedItem: Item) => void;
};

export var DEFAULT_QUERY: Query = {
   pageSize: localStorage.getItem('mmaPageSize') != null ? Number.parseInt(localStorage.getItem('mmaPageSize') || '10') : 10,
   offset: 0,
   progress: false,
   inactivatedRowsHidden: false,
};

export const useTableDataState = create<DataState>((set) => ({
   data: [],
   isLoading: false,
   query: DEFAULT_QUERY,
   setQuery: (queryOrUpdater) => {
      set((state) => {
         const newQuery = typeof queryOrUpdater === 'function' ? queryOrUpdater(state.query) : queryOrUpdater;
         const cleanQuery = { ...newQuery };
         delete cleanQuery.bomValidFrom;
         return { query: cleanQuery };
      });
   },
   total: 0,
   progress: 0,
   updateItemInTable: (itemNo: string, updatedItem: Item) =>
      set((state) => {
         const updatedData = state.data.map((item) => {
            if (item.itemNo === itemNo) {
               if (updatedItem.isVersionedItem && item.isVersionedItem) {
                  if (updatedItem.bomValidFrom === item.bomValidFrom) {
                     return updatedItem;
                  }
               } else if (!updatedItem.isVersionedItem && !item.isVersionedItem) {
                  return updatedItem;
               }
            }
            return item;
         });

         return { data: updatedData };
      }),
}));

const useAuthToken = () => {
   const { instance } = useMsal();

   const refreshToken = useCallback(async () => {
      const accessTokenRequest = {
         scopes: [env.REACT_APP_AUTH_BACKEND_API_SCOPE || ''],
         account: instance.getAllAccounts()[0],
      };

      try {
         const result = await instance.acquireTokenSilent(accessTokenRequest);
         localStorage.setItem('accessToken', result.accessToken);
         return result.accessToken;
      } catch (e) {
         instance.acquireTokenRedirect({ scopes: ['User.Read'] });
         throw e;
      }
   }, [instance]);

   return { refreshToken };
};

export const useMutationWithCacheUpdate = (successMessage: string) => {
   const queryClient = useQueryClient();
   const query = useTableDataState((state) => state.query);
   const { setItemBomVersion } = useBomVersionState();

   const onSuccess = (response: ModifyResponse, modifiedSubItems: SubItem[], successMessage: string) => {
      response.items.forEach((responseItem) => {
         if (responseItem.bomValidFrom && responseItem.itemNo) {
            setItemBomVersion(responseItem.itemNo, responseItem.bomValidFrom);
         }
      });

      ToastService.success(successMessage);

      const cleanQuery = { ...query };
      delete cleanQuery.bomValidFrom;

      void queryClient.invalidateQueries(['data', cleanQuery]);
   };

   const { mutate } = useMutation<ModifyResponse, AxiosError, Array<Record<string, any>> | Record<string, any>>({
      mutationFn: APIService.modifySubItems,
      onSuccess: (res, modifiedSubItems) => {
         onSuccess(res, flatten([modifiedSubItems]) as SubItem[], successMessage);
      },
      onError: (error: AxiosError) => {
         ToastService.error(error.message);
      },
   });

   return mutate;
};

export const useItemMutationWithCacheUpdate = (successMessage: string) => {
   const queryClient = useQueryClient();
   const query = useTableDataState((state) => state.query);
   const { setItemBomVersion } = useBomVersionState();

   const { mutate } = useMutation(APIService.setApplicableItemNetWeights, {
      onSuccess: (response) => {
         if (response?.items?.[0]?.bomValidFrom && response?.items?.[0]?.itemNo) {
            setItemBomVersion(response.items[0].itemNo, response.items[0].bomValidFrom);
         }

         const cleanQuery = { ...query };
         delete cleanQuery.bomValidFrom;

         ToastService.success(successMessage);
         queryClient.invalidateQueries(['data', cleanQuery]);
      },
      onError: (error: any) => {
         console.error('Failed to update item weights:', error);
         ToastService.error('Failed to update item weights');
      },
   });

   function mutateItem(item: Item) {
      mutate(item);
   }

   return mutateItem;
};

export const useTableData = () => {
   const [_, setQuery] = useTableDataState((state) => [state.data, state.setQuery, state.total], shallow);
   const bomVersionState = useBomVersionState.getState();
   const { refreshToken } = useAuthToken();

   useEffect(() => {
      const storedQuery = localStorage.getItem('mmaQuery');
      if (storedQuery) {
         try {
            const q = JSON.parse(storedQuery) as Query;
            delete q.bomValidFrom;
            setQuery(q);
         } catch (error) {
            console.error('Failed to parse stored query:', error);
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []); // Intentionally run once on mount

   const query = useTableDataState((state) => state.query);

   const resetSelection = useSelectionState((state) => state.resetSelection);
   const resetExpanded = useExpandedState((state) => state.resetExpanded);

   const fetchItems = async () => {
      await refreshToken();

      const cleanQuery = { ...query };
      delete cleanQuery.bomValidFrom;

      const itemResponse = await APIService.getItems(cleanQuery);
      const convertedData = itemResponse.items.map((backendItem) => {
         return convertBackendItemToFrontendItem(backendItem);
      });

      const failedVersions: string[] = [];

      const itemsWithVersions = await Promise.all(
         convertedData.map(async (item) => {
            const result: Item[] = [];

            const defaultItem = { ...item };
            defaultItem.bomValidFrom = null;
            defaultItem.isVersionedItem = false;

            if (defaultItem.subItems) {
               defaultItem.subItems = defaultItem.subItems.map((sub) => ({
                  ...sub,
                  bomValidFrom: null,
               }));
            }

            result.push(defaultItem);

            const userSelectedBomVersion = bomVersionState.getItemBomVersion(item.itemNo);

            if (userSelectedBomVersion && userSelectedBomVersion !== 'default') {
               try {
                  const versionQuery: Query = {
                     ...DEFAULT_QUERY,
                     itemNos: [item.itemNo],
                     bomValidFrom: userSelectedBomVersion,
                     pageSize: 1,
                     offset: 0,
                     progress: false,
                  };

                  const versionResponse = await APIService.getItems(versionQuery);

                  if (versionResponse.items && versionResponse.items.length > 0) {
                     const versionedItem = convertBackendItemToFrontendItem(versionResponse.items[0]);

                     versionedItem.bomValidFrom = userSelectedBomVersion;
                     versionedItem.isVersionedItem = true;
                     versionedItem.versionLabel = `BOM Version: ${userSelectedBomVersion}`;
                     versionedItem.originalItemNo = item.itemNo;
                     versionedItem.id = `${item.id}_version_${userSelectedBomVersion.replace(/[^a-zA-Z0-9]/g, '_')}`;

                     result.push(versionedItem);
                  }
               } catch (error) {
                  failedVersions.push(`${item.itemNo} (${userSelectedBomVersion})`);
                  console.error(`Failed to fetch BOM version ${userSelectedBomVersion} for item ${item.itemNo}:`, error);
               }
            }

            return result;
         })
      );

      const itemsWithBothVersions = itemsWithVersions.flat();

      if (failedVersions.length > 0) {
         const message =
            failedVersions.length > 3
               ? `${failedVersions.length} BOM versions could not be loaded`
               : `Failed to load BOM versions: ${failedVersions.join(', ')}`;
         ToastService.warning(message);
      }

      return {
         data: itemsWithBothVersions,
         total: itemResponse.total,
         progress: itemResponse.progress,
      };
   };

   const {
      status,
      data: response,
      error,
      isLoading,
   } = useQuery(['data', query], fetchItems, {
      retry: (failureCount, error) => {
         if (error instanceof Error && error.message.includes('Network')) {
            return failureCount < 3;
         }
         return false;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
   });

   useEffect(() => {
      useTableDataState.setState({ isLoading: isLoading });
   }, [isLoading]);

   useEffect(() => {
      const storedPageSize = localStorage.getItem('mmaPageSize');
      if (storedPageSize) {
         const pageSize = parseInt(storedPageSize);
         if (!isNaN(pageSize)) {
            setQuery((currentQuery) => {
               if (currentQuery.pageSize !== pageSize) {
                  const updatedQuery = { ...currentQuery, pageSize };
                  delete updatedQuery.bomValidFrom;
                  return updatedQuery;
               }
               return currentQuery;
            });
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []); // setQuery is stable from Zustand

   const [lastQuery, setLastQuery] = useState(query);

   useEffect(() => {
      if (status === 'error') {
         const errorMessage = error instanceof Error ? error.message : 'Failed to fetch data';
         ToastService.error(errorMessage);
      } else if (status === 'success' && response) {
         const queryChangedExcludingHide =
            !equals(query, lastQuery) &&
            Object.entries(query).some(([key, value]) => key !== 'inactivatedRowsHidden' && lastQuery[key as keyof Query] !== value);

         if (queryChangedExcludingHide) {
            resetExpanded();
            resetSelection(query.pageSize);
         }

         useTableDataState.setState({
            data: response.data,
            total: response.total,
            progress: response.progress,
         });

         setLastQuery(query);

         const queryToSave = { ...query, offset: 0 };
         delete queryToSave.bomValidFrom;
         localStorage.setItem('mmaQuery', JSON.stringify(queryToSave));
      }
   }, [status, response, error, lastQuery, query, resetExpanded, resetSelection]);
};
